import React, { useEffect, useRef, useState, useCallback } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import { Cancel, CheckCircle, Delete, Edit } from "@mui/icons-material";
import { Switch, Typography } from "@mui/material";
import { useStoreState, useStoreActions } from "easy-peasy";
import { formatCurrencyUSD, isFundingAmountAcceptable } from "utils/CommonUtils";
import MDInput from "components/MDInput";
import { NumericFormat } from "react-number-format";
import SyndicationForm from "./SyndicationForm";
import { MESSAGES } from "constants/Messages";

interface SyndicationProps {
  dealApplicationId: number;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
}

const Syndication: React.FC<SyndicationProps> = ({ dealApplicationId, setComponentLoading }) => {
  // Store state
  const syndicationDistribution = useStoreState(
    (state: any) => state.syndicationStore.syndicationDistribution
  );
  const investorsList = useStoreState((state: any) => state.syndicationStore.investorsList);
  const offerDetails = useStoreState((state: any) => state.offerStore.contractTerms);

  // Store actions
  const setSyndicationDistribution = useStoreActions(
    (actions: any) => actions.syndicationStore.setSyndicationDistribution
  );
  const fetchSyndicators = useStoreActions(
    (actions: any) => actions.syndicationStore.fetchSyndicators
  );

  const fetchInvestorParties = useStoreActions(
    (actions: any) => actions.syndicationStore.fetchInvestorParties
  );

  const onInit = useStoreActions((actions: any) => actions.syndicationStore.onInit);
  const updateCachedSyndicatorsOfDealApplication = useStoreActions(
    (actions: any) => actions.syndicationStore.updateCachedSyndicatorsOfDealApplication
  );
  const handleForTotalBankFeeOrInvestorEditChanges = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.handleForTotalBankFeeOrInvestorEditChanges
  );

  const handleBankFeeSwitchChanged = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.handleBankFeeSwitchChanged
  );

  const updateManualBankFee = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.updateManualBankFee
  );

  const refreshHeldBackStore = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.refresh
  );
  const saveSyndicators = useStoreActions(
    (actions: any) => actions.syndicationStore.saveSyndicators
  );
  const save = useStoreActions((actions: any) => actions.syndicationStore.save);

  const setDisplayAlert = useStoreActions(
    (actions: any) => actions.syndicationStore.setDisplayAlert
  );
  const fetchRecalculatedApplication = useStoreActions(
    (actions: any) => actions.offerStore.fetchRecalculatedApplication
  );
  const setLoading = useStoreActions((actions: any) => actions.offerStore.setLoading);

  const heldbacks = useStoreState((state: any) => state.heldBackCommissionStore.heldbacks);

  const getContractTerms = useStoreActions((actions: any) => actions.offerStore.getContractTerms);
  const saveHeldback = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.saveHeldback
  );
  const setSyndicationBankFeeAlert = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.setSyndicatorBankFeeAlert
  );
  const offersData = useStoreState((state: any) => state.offerStore.offers);

  const [mode, setMode] = useState<"add" | "edit" | "">("");
  const [editingRow, setEditingRow] = useState<any>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [cachedSyndData, setCachedSyndData] = useState<any[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const syndicationBankFeeAlert = useStoreState(
    (state: any) => state.heldBackCommissionStore.syndicatorBankFeeAlert
  );

  useEffect(() => {
    try {
      setComponentLoading("syndication", true);
      onInit(dealApplicationId);
    } finally {
      setComponentLoading("syndication", false);
    }
  }, [dealApplicationId, onInit]);

  useEffect(() => {
    setCachedSyndData(syndicationDistribution);
  }, [syndicationDistribution]);

  const debounce = (func: Function, delay: number) => {
    return (...args: any[]) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedBackendCall = useCallback(
    async (rowData: any) => {
      if (!rowData) return;

      let syndicatorsUpdated = [...syndicationDistribution];

      if (mode === "add") {
        const isNewRow = !syndicationDistribution.some(
          (row: any) => row.applicationPartyId === rowData.applicationPartyId
        );

        if (isNewRow) {
          const newRow = {
            ...rowData,
            partyId: rowData?.applicationPartyId,
            tenantId: 1,
            applicationId: Number(dealApplicationId),
          };
          syndicatorsUpdated.push(newRow);
        }
      } else {
        if (rowData?.applicationPartyId != null) {
          syndicatorsUpdated = syndicatorsUpdated.filter(
            (row) => row?.applicationPartyId !== rowData.applicationPartyId
          );
        } else {
          syndicatorsUpdated = syndicatorsUpdated.filter((row) => row?.id !== rowData.id);
        }
        const updatedRow = {
          ...rowData,
          id: rowData.id || undefined,
          partyId: rowData.partyId || rowData.applicationPartyId,
          tenantId: rowData.tenantId || 1,
          applicationId: rowData.applicationId || Number(dealApplicationId),
        };

        syndicatorsUpdated.push(updatedRow);
      }

      try {
        setLoading(true);
        const response = await updateCachedSyndicatorsOfDealApplication({
          applicationId: dealApplicationId,
          syndicators: syndicatorsUpdated,
        });

        setCachedSyndData(response);

        if (rowData) {
          let matchedSyndicator = null;

          if (rowData.newSyndicatorParty) {
            matchedSyndicator = response.find(
              (row: any) => row.newSyndicatorParty === rowData.newSyndicatorParty
            );
          }

          if (!matchedSyndicator && rowData.applicationPartyId) {
            matchedSyndicator = response.find(
              (row: any) => row.applicationPartyId === rowData.applicationPartyId
            );
          }

          if (matchedSyndicator) {
            setEditingRow(matchedSyndicator);
          }
        }
      } catch (error) {
        console.error("Error in backend call:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      mode,
      syndicationDistribution,
      dealApplicationId,
      updateCachedSyndicatorsOfDealApplication,
      setLoading,
    ]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const bankFeeExists = () => {
    return offerDetails && offerDetails["bankFee"] && offerDetails["bankFee"] > 0;
  };

  const handleAddSyndication = () => {
    const hasAcceptedOffer = offersData?.some((offer: any) => offer.status === "Accepted");

    if (!hasAcceptedOffer) {
      setSyndicationBankFeeAlert("Please accept an offer before proceeding with syndication.");
      return;
    }

    const newSyndication = {
      applicationPartyName: "",
      applicationPartyId: "",
      investmentPercentage: "",
      fundedAmount: "",
      startingExposure: "",
      expectedProfit: "",
    };
    setMode("add");
    setEditingRow(newSyndication);
    setIsEditing(true);
  };

  const investorOptions = investorsList
    ?.filter(
      (inv: any) =>
        (editingRow && editingRow.partyId === inv.key) ||
        (syndicationDistribution?.length
          ? syndicationDistribution.every((synd: any) => synd.partyId !== inv.key)
          : true)
    )
    .map((investor: any) => ({
      label: investor?.name,
      value: investor?.key,
      fund: investor?.fund,
    }));

  console.log(investorOptions);

  const customButton = (
    <MDBox display="flex" gap={2}>
      <MDButton
        color="primary"
        variant="contained"
        size="medium"
        sx={{ textTransform: "none" }}
        onClick={handleAddSyndication}
      >
        + Add Syndicator
      </MDButton>
    </MDBox>
  );

  const [editingBankFee, setEditingBankFee] = useState<{ partyId: string | null; value: number }>({
    partyId: null,
    value: 0,
  });

  const handleStartEditingBankFee = (partyId: string, currentValue: number) => {
    setEditingBankFee({
      partyId,
      value: currentValue,
    });
  };

  const refresh = async (dealApplicationId: any) => {
    await refreshHeldBackStore(dealApplicationId);
    await fetchRecalculatedApplication(dealApplicationId);
    await getContractTerms(dealApplicationId);
  };

  const handleAddNew = (value: string): boolean => {
    let updatedRow = { ...editingRow };

    if (!value) {
      updatedRow["newSyndicatorParty"] = null;
      updatedRow["addNewSyndicatorParty"] = false;
      updatedRow["showAddingNew"] = false;
      updatedRow["partyId"] = null;
      updatedRow["applicationPartyName"] = null;
      updatedRow["applicationPartyId"] = null;
      setEditingRow(updatedRow);
      return true;
    }

    const nameExists = investorsList?.some(
      (inv: any) => inv?.name?.toLowerCase() === value.toLowerCase()
    );

    if (nameExists) {
      return false;
    }

    updatedRow["newSyndicatorParty"] = value;
    updatedRow["addNewSyndicatorParty"] = true;
    updatedRow["partyId"] = null;
    updatedRow["applicationPartyName"] = null;
    updatedRow["applicationPartyId"] = null;
    setEditingRow(updatedRow);

    return true;
  };

  const onSave = async () => {
    try {
      setLoading(true);
      console.log("editing row", editingRow);
      const type = editingRow?.type;

      const request: any = {
        amount: editingRow.amount,
        applicationId: editingRow.applicationId,
        description: editingRow.description || "",
        name: editingRow.applicationPartyName,
        partyId: editingRow.applicationPartyId,
        tenantId: editingRow.tenantId,
        type: type,
      };

      if (editingRow.id) {
        request.id = editingRow.id;
      }

      if (editingRow.oldAmount) {
        request.oldAmount = editingRow.oldAmount;
      }

      if (editingRow.isInvested) {
        request.oldAmount = editingRow.oldAmount;
      }

      if (
        editingRow.applicationPartyId &&
        editingRow.oldPartyId &&
        editingRow.applicationPartyId !== editingRow.oldPartyId
      ) {
        request.oldPartyId = editingRow.oldPartyId;
      }
      const offerDetails = await getContractTerms(dealApplicationId);
      await saveHeldback({
        offerDetails: offerDetails,
        heldbackData: request,
        dealApplicationId: dealApplicationId,
        callback: async () => {
          await refresh(dealApplicationId);
        },
      });
    } finally {
      setEditingRow(null);
      setIsEditing(false);
      setLoading(false);
    }
  };

  const handleSaveBankFee = async (partyId: any, row: any) => {
    try {
      setLoading(true);
      const matchingHeldback = heldbacks
        ?.filter((row: any) => !row.defaultSyndicator && row.type === "bank_fee")
        .find((heldback: any) => heldback.applicationPartyId === partyId);
      matchingHeldback.oldAmount = matchingHeldback.amount;
      matchingHeldback.amount = editingBankFee.value;
      const type = matchingHeldback?.type;

      const request: any = {
        amount: matchingHeldback.amount,
        applicationId: matchingHeldback.applicationId,
        description: matchingHeldback.description || "",
        name: matchingHeldback.applicationPartyName,
        partyId: matchingHeldback.applicationPartyId,
        tenantId: matchingHeldback.tenantId,
        type: type,
      };

      if (matchingHeldback.id) {
        request.id = matchingHeldback.id;
      }

      if (matchingHeldback.oldAmount) {
        request.oldAmount = matchingHeldback.oldAmount;
      }

      if (matchingHeldback.isInvested) {
        request.isInvested = matchingHeldback.isInvested;
      }
      const offerDetails = await getContractTerms(dealApplicationId);

      await updateManualBankFee({
        offerDetails: offerDetails,
        investor: row,
        dealApplicationId: dealApplicationId,
        heldback: matchingHeldback,
        callback: async () => {
          await refresh(dealApplicationId);
        },
        skipPartyId: [],
      });
    } finally {
      setEditingBankFee({ partyId: null, value: 0 });
      setLoading(false);
    }
  };

  const handleCancelEditBankFee = () => {
    setEditingBankFee({ partyId: null, value: 0 });
  };

  const handleBankFeeValueChange = (value: number) => {
    setEditingBankFee((prev) => ({
      ...prev,
      value,
    }));
  };

  const handleBankFeeSwitch = async (checked: boolean, row: any) => {
    try {
      setLoading(true);
      const contractTerms = offerDetails?.contractTerms;
      const funder = contractTerms?.funder;
      const defaultSyndicatorPartyId = funder?.defaultSyndicatorId ?? funder?.id;

      const updatedInvestor = {
        ...row,
        defaultSyndicatorPartyId,
        totalBankFee: offerDetails?.bankFee || 0,
        isBankFee: checked,
      };

      if (updatedInvestor.totalBankFee === 0 || updatedInvestor.totalBankFee === "") {
        setDisplayAlert(true);
        return {
          ...updatedInvestor,
          isBankFee: false,
        };
      }
      await handleBankFeeSwitchChanged({
        offerDetails,
        investor: updatedInvestor,
        dealApplicationId,
      });
      await refreshHeldBackStore(dealApplicationId);
      await onInit(dealApplicationId);
      return updatedInvestor;
    } finally {
      setLoading(false);
    }
  };

  const handleSyndicatorDelete = async (row: any) => {
    try {
      setLoading(true);
      let cachedDeletedSyndicator = [];
      if (row?.id) {
        cachedDeletedSyndicator.push(row["id"]);
      }
      const updateSyndicators = cachedSyndData.filter((synd: any) => synd.id !== row?.id);
      const response = await updateCachedSyndicatorsOfDealApplication({
        applicationId: dealApplicationId,
        syndicators: updateSyndicators,
      });
      await handleSaveSyndication(response, cachedDeletedSyndicator);
    } catch (error) {
      console.error("Error in handleSyndicatorDelete:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSaveSyndication = async () => {
    try {
      setLoading(true);

      let syndRequest = cachedSyndData;
      let deletingId: any = [];

      if (mode === "edit" && editingRow) {
        deletingId = [editingRow?.id];
        syndRequest = cachedSyndData.filter((row: any) => row.id !== editingRow.id);
        const editedSynd = cachedSyndData.find((row: any) => row.id === editingRow.id);
        editedSynd["applicationPartyId"] = editingRow?.applicationPartyId;
        editedSynd["partyId"] = editingRow?.applicationPartyId;
        editedSynd["id"] = null;
        syndRequest.push(editedSynd);
      }
      await handleSaveSyndication(cachedSyndData, deletingId);
    } finally {
      await fetchInvestorParties("1");
      setLoading(false);
      setEditingRow(null);
      setIsEditing(false);
      setMode("");
    }
  };
  const handleSaveSyndication = async (syndicationData: any, deletedSyndicators: any) => {
    try {
      let percentageChangedPartyIds: any[] = [];
      if (offerDetails && offerDetails.bankFee !== "" && parseFloat(offerDetails.bankFee) > 0) {
        const isChanged = await checkIfBankFeeManualyPercentageChanged(
          syndicationData,
          percentageChangedPartyIds
        );

        if (isChanged) {
          setDisplayAlert(MESSAGES.BANK_FEE_ALERT_MESSAGE);
        } else {
          await handleBankFeeAlert(
            offerDetails,
            false,
            syndicationData,
            deletedSyndicators,
            percentageChangedPartyIds
          );
        }
      } else {
        await saveSyndicators({
          cachedSyndicationDistribution: syndicationData,
          deletedSyndicators: deletedSyndicators,
        });
        await fetchRecalculatedApplication(dealApplicationId);
        await fetchSyndicators(dealApplicationId);
        await refreshHeldBackStore(dealApplicationId);
      }
    } catch (error) {
      console.error("Error in handleSaveSyndication:", error);
    }
  };

  const checkIfBankFeeManualyPercentageChanged = async (
    cachedSyndication: any,
    percentageChangedPartyIds: any[]
  ): Promise<boolean> => {
    try {
      let status = false;
      for (let investor of syndicationDistribution) {
        if (investor.isManualBankFeeChanged && !investor.defaultSyndicator) {
          if (cachedSyndication.length > 0) {
            for (let cachedInvestor of cachedSyndication) {
              if (investor.partyId === cachedInvestor.partyId) {
                const invPercentage = parseFloat(investor.investmentPercentage).toFixed(2);
                const cachedInvPercentage = parseFloat(cachedInvestor.investmentPercentage).toFixed(
                  2
                );
                if (invPercentage !== cachedInvPercentage && investor.isBankFee) {
                  status = true;
                  percentageChangedPartyIds.push(investor.partyId);
                  break;
                }
              }
            }
          }
        }
      }
      return status;
    } catch (error) {
      console.error("Error in checkIfBankFeeManualyPercentageChanged:", error);
      return false;
    }
  };

  const handleBankFeeAlert = async (
    offerDetails: any,
    bankFeeUpdate: boolean,
    syndicationData: any,
    deletedSyndicators: any,
    percentageChangedPartyIds: any[]
  ) => {
    const sleep = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms));
    try {
      const partyIds = await getManualBankFeeChangedApplicationPartyIds(
        bankFeeUpdate,
        percentageChangedPartyIds
      );

      await save({
        syndication: syndicationData,
        applicationId: dealApplicationId,
        deletingIds: deletedSyndicators,
      });

      if (offerDetails) {
        await handleForTotalBankFeeOrInvestorEditChanges({
          offerDetails: offerDetails,
          dealApplicationId: dealApplicationId,
          callback: undefined,
          skipPartyId: partyIds,
        });

        await sleep(1000);
        await fetchRecalculatedApplication(dealApplicationId);
        await refreshHeldBackStore(dealApplicationId);
      }
    } catch (error) {
      console.error("Error in handleBankFeeAlert:", error);
    }
  };

  const getManualBankFeeChangedApplicationPartyIds = async (
    bankFeeUpdate: boolean,
    percentageChangedPartyIds: any[]
  ): Promise<any[]> => {
    try {
      let partyIds: any[] = [];
      let oldInvestors = syndicationDistribution;

      for (let oldInvestor of oldInvestors) {
        if (oldInvestor.isManualBankFeeChanged && !oldInvestor.defaultSyndicator) {
          if (bankFeeUpdate) {
            if (!percentageChangedPartyIds.includes(oldInvestor.partyId)) {
              partyIds.push(oldInvestor.partyId);
            }
          } else {
            partyIds.push(oldInvestor.partyId);
          }
        }
      }

      return partyIds;
    } catch (error) {
      console.error("Error in getManualBankFeeChangedApplicationPartyIds:", error);
      return [];
    }
  };

  const handleEditSyndication = (row: any | null) => {
    setMode("edit");
    setIsEditing(true);
    setEditingRow(row ? { ...row } : {});
  };

  const handleCancelEdit = () => {
    setCachedSyndData(syndicationDistribution);
    setEditingRow(null);
    setIsEditing(false);
    setMode("");
  };

  const handleSelectRow = (rowKey: number) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(rowKey)
        ? prevSelected.filter((key) => key !== rowKey)
        : [...prevSelected, rowKey]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === cachedSyndData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(cachedSyndData.map((row: any) => row.id));
    }
  };

  const handleInputChange = (value: any, field: string) => {
    const fundingAmount = offerDetails?.fundedAmount;
    let updatedRow = { ...editingRow };

    if (field === "addNew") {
      updatedRow["name"] = null;
      updatedRow["partyId"] = null;
      updatedRow["applicationPartyName"] = null;
      updatedRow["applicationPartyId"] = null;
      updatedRow["investmentPercentage"] = undefined;
      updatedRow["fundedAmount"] = null;
      updatedRow.startingExposure = null;
      updatedRow.expectedProfit = null;
      updatedRow.showAddingNew = true;
      setEditingRow(updatedRow);
      return;
    }

    const numericValue = typeof value === "string" ? parseFloat(value) || 0 : value;

    if (field === "fundedAmount" && fundingAmount && !isNaN(fundingAmount)) {
      const isValid = isFundingAmountAcceptable(
        fundingAmount,
        updatedRow,
        cachedSyndData,
        numericValue
      );

      if (!isValid) {
        setSyndicationBankFeeAlert(MESSAGES.PLEASE_ADJUST_FUNDING_AMOUNT);
        return;
      }

      const investmentPercent = (numericValue / fundingAmount) * 100;
      const formattedPercent = parseFloat(investmentPercent.toFixed(4));

      updatedRow = {
        ...updatedRow,
        investmentPercentage: formattedPercent,
        [field]: numericValue,
      };
    } else if (field === "investmentPercentage" && fundingAmount && !isNaN(fundingAmount)) {
      const investedAmount = (numericValue / 100) * fundingAmount;
      const formattedInvestedAmount = parseFloat(investedAmount.toFixed(2));

      const isValid = isFundingAmountAcceptable(
        fundingAmount,
        updatedRow,
        cachedSyndData,
        investedAmount
      );

      if (!isValid) {
        setSyndicationBankFeeAlert(MESSAGES.PLEASE_ADJUST_FUNDING_AMOUNT);
        return;
      }

      updatedRow = {
        ...updatedRow,
        fundedAmount: formattedInvestedAmount,
        [field]: numericValue,
      };
    } else {
      updatedRow = {
        ...updatedRow,
        [field]: numericValue,
      };
    }

    setEditingRow(updatedRow);

    if ((field === "fundedAmount" || field === "investmentPercentage") && !isNaN(numericValue)) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        debouncedBackendCall(updatedRow);
      }, 1000);
    }
  };

  const isInputColumnDisabled = () => {
    return (
      (editingRow && editingRow.applicationCommissionId ? true : false) ||
      editingRow?.isFromCommission ||
      editingRow?.defaultSyndicator
    );
  };

  const columns = [
    {
      Header: "SYNDICATOR NAME",
      accessor: "applicationPartyName",
      selector: "applicationPartyId",
      disabled: isInputColumnDisabled,
      align: "left",
      input: "select",
      width: "250px",
      options: investorOptions,
    },
    {
      Header: "INVESTMENT %",
      accessor: "investmentPercentage",
      align: "left",
      input: "number",
      disabled: isInputColumnDisabled,
    },
    {
      Header: "INVESTMENT AMOUNT",
      accessor: "fundedAmount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.fundedAmount),
      align: "left",
      input: "amount",
      disabled: isInputColumnDisabled,
    },
    {
      Header: "PAYBACK AMOUNT (RTR)",
      accessor: "paybackAmount",
      input: "amount",
      disabled: () => true,
      required: false,
      Cell: (row: any) =>
        formatCurrencyUSD(
          (isNaN(row?.row?.fundedAmount) ? 0 : parseFloat(row?.row?.fundedAmount)) +
            (isNaN(row?.row?.expectedProfit) ? 0 : parseFloat(row?.row?.expectedProfit))
        ),
      align: "left",
    },
    {
      Header: "Starting Exposure",
      accessor: "startingExposure",
      disabled: () => true,
      required: false,
      input: "amount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.startingExposure),
      align: "left",
    },
    {
      Header: "BANK FEE",
      accessor: "",
      width: "200px",
      disabled: () => true,
      required: false,
      hidden: true,
      align: "left",
      Cell: ({ row }: any) => {
        if (row?.defaultSyndicator) {
          return <></>;
        }

        const matchingHeldback = heldbacks
          ?.filter((row: any) => !row.defaultSyndicator)
          .find((heldback: any) => heldback.applicationPartyId === row.partyId);

        const isEditing = editingBankFee.partyId === row.partyId;
        const currentValue = isEditing ? editingBankFee.value : matchingHeldback?.amount || 0;

        return (
          <MDBox display="flex" alignItems="center">
            <Switch
              checked={(bankFeeExists() && row.isBankFee) || false}
              disabled={!bankFeeExists()}
              onChange={(e) => {
                const checked = e?.target.checked;
                handleBankFeeSwitch(checked, row);
              }}
              color="primary"
            />
            {row.isBankFee && (
              <MDBox display="flex" gap={1} alignItems="center">
                {isEditing ? (
                  <>
                    <NumericFormat
                      variant="standard"
                      value={currentValue}
                      thousandSeparator
                      prefix="$"
                      decimalScale={2}
                      fixedDecimalScale
                      allowNegative={false}
                      customInput={MDInput}
                      onValueChange={({ value }: any) => {
                        const formattedValue = parseFloat(value.replace(/[^0-9.-]+/g, "")) || 0;
                        handleBankFeeValueChange(formattedValue);
                      }}
                      inputProps={{
                        style: { fontSize: 14, fontWeight: 700 },
                      }}
                      sx={{ flexBasis: "70%" }}
                    />
                    <CheckCircle
                      onClick={() => handleSaveBankFee(row.partyId, row)}
                      sx={{
                        cursor: "pointer",
                        color: "success.main",
                        fontSize: "20px",
                      }}
                    />
                    <Cancel
                      onClick={handleCancelEditBankFee}
                      sx={{
                        cursor: "pointer",
                        color: "error.main",
                        fontSize: "20px",
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        minWidth: "80px",
                        textAlign: "right",
                      }}
                    >
                      {formatCurrencyUSD(currentValue)}
                    </Typography>
                    <Edit
                      onClick={() =>
                        handleStartEditingBankFee(row.partyId, matchingHeldback?.amount || 0)
                      }
                      sx={{ cursor: "pointer" }}
                    />
                  </>
                )}
              </MDBox>
            )}
          </MDBox>
        );
      },
    },
    {
      Header: "EXPECTED PROFIT",
      accessor: "expectedProfit",
      disabled: () => true,
      required: false,
      input: "amount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.expectedProfit),
    },
  ];

  const renderTotal = (key: any) => {
    let total = 0;

    if (editingRow && editingRow[key]) {
      const editingAmount = parseFloat(editingRow[key]);
      if (!isNaN(editingAmount)) {
        total += editingAmount;
      }
    }

    cachedSyndData
      .filter((row: any) => !editingRow || editingRow.id !== row.id)
      .forEach((row: any) => {
        const rowAmount = parseFloat(row[key]);
        if (!isNaN(rowAmount)) {
          total += rowAmount;
        }
      });

    return key.includes("Amount")
      ? `${formatCurrencyUSD(total.toFixed(2))}`
      : key.includes("Percentage")
      ? `${total.toFixed(2)} %`
      : total.toFixed(2);
  };

  return (
    <CollapsibleCard header={"Syndication"} customButton={customButton}>
      <MDBox paddingRight={3}>
        <SyndicationForm
          rowKey={"id"}
          columns={columns}
          data={cachedSyndData}
          onDelete={handleSyndicatorDelete}
          onEdit={handleEditSyndication}
          onSave={onSaveSyndication}
          onCancel={handleCancelEdit}
          editingRow={editingRow}
          isEditing={isEditing}
          onInputChange={handleInputChange}
          selectedRows={selectedRows}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          onSort={() => console.log("cancel")}
          getRowStyle={(row: any) => "regular"}
          isActionsDisabled={undefined}
          renderTotal={renderTotal}
          totalColumn={["investmentPercentage", "fundedAmount"]}
          handleAddNew={handleAddNew}
        />
      </MDBox>
    </CollapsibleCard>
  );
};

export default Syndication;
